import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Avatar
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { useCurrentUser } from '../../../hooks/users/queries';
import { LINK_COLOR } from '../../../constants/ColorConstants';

// Configure marked with custom renderer for excerpt links
marked.use({
  breaks: true,  // Convert \n to <br>
  gfm: true,     // GitHub Flavored Markdown
  renderer: {
    // marked v17+ passes an object with { href, title, text, tokens }
    link({ href, title, text }) {
      // Check if this is an excerpt link with pattern #excerpt:start:end
      const excerptMatch = href?.match(/#excerpt:(\d+):(\d+)/);

      if (excerptMatch) {
        const [_, start, end] = excerptMatch;
        // Add data attributes for click handling
        return `<a href="#" data-excerpt-start="${start}" data-excerpt-end="${end}" class="excerpt-link">${text}</a>`;
      }

      // Default link rendering
      const titleAttr = title ? ` title="${title}"` : '';
      return `<a href="${href}"${titleAttr}>${text}</a>`;
    }
  }
});

const ChatMessage = ({ message, transcriptId }) => {
  const isUser = message.role === 'user';
  const { data: currentUser } = useCurrentUser();
  const history = useHistory();

  // Get user initials for avatar
  const getUserInitials = () => {
    if (currentUser?.name) {
      const names = currentUser.name.split(' ');
      if (names.length >= 2) {
        return names[0][0] + names[1][0];
      }
      return names[0][0];
    }
    return currentUser?.email?.[0]?.toUpperCase() || 'U';
  };

  // Memoize the parsed HTML to avoid re-parsing on every render
  const htmlContent = useMemo(() => {
    if (!message.content) return '';
    const rawHtml = marked.parse(message.content);
    return DOMPurify.sanitize(rawHtml, {
      ADD_ATTR: ['data-excerpt-start', 'data-excerpt-end'],
    });
  }, [message.content]);

  // Handle excerpt link clicks via event delegation
  const handleClick = useCallback((e) => {
    const link = e.target.closest('.excerpt-link');
    if (link) {
      e.preventDefault();
      const start = link.dataset.excerptStart;
      const end = link.dataset.excerptEnd;

      // Navigate to the excerpt position using React Router
      const currentPath = window.location.pathname;
      const targetPath = `/transcripts/${transcriptId}`;

      if (currentPath === targetPath) {
        // Use React Router's history.replace to update URL and trigger effects
        history.replace(`${targetPath}?pos=${start}&end=${end}`);
      } else {
        // Navigate to different transcript
        history.push(`${targetPath}?pos=${start}&end=${end}`);
      }
    }
  }, [history, transcriptId]);

  if (isUser) {
    // User message with avatar inside bubble
    return (
      <Box>
        <Box
          sx={{
            backgroundColor: '#F3F4F6',
            borderRadius: 2,
            padding: '10px 14px',
            display: 'inline-flex',
            gap: 1.5,
            alignItems: 'center',
            maxWidth: '100%'
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: '14px',
              backgroundColor: '#3B82F6',
              flexShrink: 0
            }}
          >
            {getUserInitials()}
          </Avatar>
          <Box
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: '14px'
            }}
          >
            {message.content}
          </Box>
        </Box>
      </Box>
    );
  }

  if (!message.content) return <div />

  // Assistant message - use dangerouslySetInnerHTML with sanitized marked output
  return (
    <Box sx={{ mb: 2 }}>
      <Box
        onClick={handleClick}
        sx={{
          fontSize: '14px',
          '& h1': { mt: 2, mb: 1, fontSize: '1.75rem' },
          lineHeight: 1.6,
          '& h2': { mt: 2, mb: 1, fontSize: '1.5rem' },
          '& h3': { mt: 2, mb: 1, fontSize: '1.25rem' },
          '& p': { mb: 1 },
          '& ul, & ol': { mb: 1, pl: 3 },
          '& li': { mb: 0.5 },
          '& blockquote': {
            borderLeft: '3px solid',
            borderColor: 'divider',
            pl: 2,
            ml: 0,
            my: 1
          },
          '& a': {
            color: LINK_COLOR,
            textDecoration: 'none',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline'
            }
          }
        }}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    </Box>
  );
};

export default ChatMessage;
